import { useEffect, useMemo, useState } from 'react';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import { BundleStatus } from 'generated/graphql';
import { Control, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  getBundleFormDataByBundleId,
  getMutationErrors,
  MutationErrors,
  toSavedContentIds,
  toSavedProgramIds,
} from '../model/otpBundles.model.mapper';
import uuid4 from 'uuid4';
import auth from '@client/components/auth';
import { isEmpty, isNil } from 'lodash';
import {
  FormCta,
  toFormContent,
  toFormContentEmptyState,
  toFormCta,
  toFormFeatures,
  toFormOnChangeArray,
  toFormPrograms,
  toFormProgramsEmptyState,
  toFormSelectedContent,
  toFormSelectedPrograms,
  trackRedirectEvent,
} from './otpBundle.vm.mapper';
import { navigate, PATHS } from '@client/routes';
import { getHash, updateHash } from '../../common/helpers';
import { trackEvent } from '@services/tracking';
import { OtpBundlesModel } from '../model/otpBundles.model';
import { FormDataMultiSelect } from './otpBundles.viewModel';
import {
  toCreateBundlePayload,
  toUpdateBundlePayload,
  validateContentForPublish,
} from './editBundle.vm.mapper';
import { consumeOfferPrefillFromProgram } from '../../common/offerPrefill';

type FormMode = 'edit' | 'create' | 'duplicate' | null;

export type NewBundleSubmit = Array<{ id: string; title: string }>;
export type EditBundleSubmit = Array<{ id: number; title: string }>;

export type EditOtpBundleData = {
  submittedNewBundle: NewBundleSubmit;
  submittedEditBundle: EditBundleSubmit;
  addOtpBundle: () => void;
  edit: (id: number) => void;
  duplicate: (id: number) => void;
  editLP: (id: number) => void;
  formData: {
    creatorId: number;
    readonly: boolean;
    showFeeControl: boolean;
    control: Control<BundleFormValues>;
    open: boolean;
    cancel: () => void;
    title: string;
    cta: FormCta;
    formError: string | string[] | null;
    formErrorTitle: string | null;
    data: {
      programs: FormDataMultiSelect<number>;
      content: FormDataMultiSelect<string>;
      features: {
        options: Array<{
          name: string;
          id: number;
        }>;
        toFormOnChange: (value: number, selectedValues: number[]) => number[];
      };
    };
    discounts?: {
      count: number;
      onShow: () => void;
      onCreate: () => void;
    };
    isFreeDisabled: boolean;
    isOfferFree: boolean;
    onChangeIsFree: (value: boolean) => void;
    onChangeHideOffer: (value: boolean) => void;
  };
  deleteModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
  };
  archiveModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
  };
  publishModal: {
    isOpen: boolean;
    confirm: () => void;
    cancel: () => void;
    draftItems: string[];
  };
  unsavedChangesModal: {
    isOpen: boolean;
    saveChanges: () => void;
    discard: () => void;
    cancel: () => void;
  };
};

type PayloadMap = {
  addOtpBundle: null;
  saveBundleWithAction: { callback: () => void };
  bundleForm: {
    mode: FormMode;
    bundleId: number | null;
  };
  cancelBundleChanges: null;
  confirmRedirect: string;
  confirmSaveChanges: null;
  confirmOfferChanges: null;
  redirect: string;
  discardChanges: null;
  duplicateBundle: number;
  saveBundle: null;
  publishBundle: null;
  archiveBundle: null;
  confirmArchiveBundle: null;
  confirmPublishBundle: null;
  deleteBundle: null;
  confirmDeleteBundle: null;
  editLandingPage: number;
  editBundle: number;
};

type EditOtpBundleVMAction = Actions<PayloadMap>;
export type EditOtpBundleVMDispatcher = FSpread<PayloadMap>;

export type EditOtpBundleVM = EditOtpBundleData;

export const coverImageSchema = z.object({
  image: z.string(),
  cdn01: z.string(),
});

export type CoverImage = z.infer<typeof coverImageSchema>;

const otpBundleBaseSchema = z.object({
  name: z.string().trim().min(1, { message: 'Required' }),
  description: z.string().optional(),
  coverImage: coverImageSchema.nullable().optional(),
  programs: z.array(z.number()).nullable().optional(),
  content: z.array(z.string()).nullable().optional(),
  features: z.array(z.number()).nullable().optional(),
  playbookFee: z.number().optional(),
  hideOffer: z.boolean().default(false),
});

export const otpBundleSchema = z.discriminatedUnion('isFree', [
  otpBundleBaseSchema.extend({
    isFree: z.literal(true),
    price: z.string().optional(),
  }),
  otpBundleBaseSchema.extend({
    isFree: z.literal(false),
    price: z.string().trim().min(1, { message: 'Required' }),
  }),
]);

export type BundleFormValues = z.infer<typeof otpBundleSchema>;

type State = {
  formMode: FormMode;
  bundleId: null | number;
  formError: string | string[] | null;
  submittedNewBundle: NewBundleSubmit;
  submittedEditBundle: EditBundleSubmit;
  submittingBundle: boolean;
  confirmDelete: boolean;
  confirmArchive: boolean;
  confirmPublishContent: string[];
  redirectPath: string | null;
  confirmSaveChanges: boolean;
};

const formDefaultValues = {
  name: '',
  price: '',
  isFree: false,
  description: '',
  coverImage: null,
  programs: [],
  features: [],
  content: [],
  published: false,
  archived: false,
  playbookFee: undefined,
  hideOffer: false,
};

export const useEditOtpBundleVM = ({
  creatorId,
  model,
}: {
  creatorId: number;
  model: Async<OtpBundlesModel>;
}): EditOtpBundleVM => {
  const isAdmin = auth.isAdministrator();
  const modelValue = model.type === 'fetched' ? model.value : null;

  const [state, setState] = useState<State>({
    formMode: null,
    bundleId: null,
    formError: null,
    submittedNewBundle: [],
    submittedEditBundle: [],
    submittingBundle: false,
    confirmDelete: false,
    confirmArchive: false,
    confirmPublishContent: [],
    redirectPath: null,
    confirmSaveChanges: false,
  });

  const initialOtpBundles = modelValue?.otpBundles || [];
  const allPrograms = modelValue?.allPrograms || [];
  const sellablePrograms = modelValue?.sellablePrograms || [];
  const allFeatures = modelValue?.allFeatures || [];
  const allContent = modelValue?.allContent || [];
  const sellableContent = modelValue?.sellableContent || [];
  const discounts = modelValue?.discounts || [];
  const rawContent = modelValue?.rawContent || [];

  useEffect(() => {
    if (model.type !== 'fetched') return;

    const paramBundleId = getHash();
    if (paramBundleId && !isNaN(Number(paramBundleId))) {
      setBundleForm({
        mode: 'edit',
        bundleId: paramBundleId,
      });
      return;
    }

    const prefill = consumeOfferPrefillFromProgram();
    if (prefill) {
      reset(
        {
          ...formDefaultValues,
          name: prefill.name,
          description: prefill.description ?? '',
          coverImage: prefill.coverImage,
          programs: [prefill.programId],
        },
        { keepValues: false, keepDirty: false, keepDirtyValues: false },
      );
      setState((prev) => ({
        ...prev,
        formMode: 'create',
        bundleId: null,
        formError: null,
      }));
    }
  }, [model.type]);

  const editedBundle = useMemo(
    () =>
      isNil(state?.bundleId)
        ? null
        : (modelValue?.otpBundles || []).find((b) => b.id === state.bundleId),
    [state.bundleId, modelValue?.otpBundles],
  );

  const {
    handleSubmit,
    watch,
    control,
    reset,
    setValue,
    clearErrors,
    formState: { isDirty },
  } = useForm<BundleFormValues>({
    defaultValues: formDefaultValues,
    resolver: zodResolver(otpBundleSchema),
  });

  const isFree = watch('isFree');

  const onChangeIsFree = (value: boolean) => {
    trackEvent({
      type: 'Offer set to free',
      payload: {
        offerId: state.bundleId || 0,
        isFree: value,
      },
    });

    if (value) {
      clearErrors('price');
      setValue('price', '');
    }
  };

  const onChangeHideOffer = (value: boolean) => {
    trackEvent({
      type: 'Hide offer',
      payload: {
        offerId: state.bundleId || 0,
        isHidden: value,
      },
    });
  };

  const features = watch('features') || [];
  const programs = watch('programs') || [];
  const content = watch('content') || [];

  const published =
    state.formMode === 'edit'
      ? editedBundle?.status === BundleStatus.Published
      : false;
  const archived =
    state.formMode === 'edit'
      ? editedBundle?.status === BundleStatus.Archived
      : false;
  const wasPurchased =
    state.formMode === 'edit' ? !!editedBundle?.wasPurchased : false;
  const discountsCount = !editedBundle?.id
    ? 0
    : discounts.filter((d) =>
        (d?.paidProductIds || [])?.includes(editedBundle?.id),
      ).length;

  const dispatch: EditOtpBundleVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as EditOtpBundleVMAction;
    switch (action.type) {
      case 'saveBundle':
        trackEvent({
          type: 'Offer updated',
          payload: {
            offerId: state.bundleId || 0,
          },
        });
        setState((prev) => ({ ...prev, submittingBundle: true }));
        saveBundle();
        return null;
      case 'saveBundleWithAction':
        setState((prev) => ({ ...prev, submittingBundle: true }));
        saveBundle().then(() => action.payload.callback());
        return null;
      case 'publishBundle':
        setState((prev) => ({ ...prev, submittingBundle: true }));
        publishBundle();
        return null;
      case 'archiveBundle':
        setState((prev) => ({
          ...prev,
          confirmArchive: true,
        }));
        return null;
      case 'deleteBundle':
        setState((prev) => ({
          ...prev,
          confirmDelete: true,
        }));
        return null;
      case 'bundleForm':
        setBundleForm(action.payload);
        return null;
      case 'cancelBundleChanges':
        if (!isDirty) {
          setBundleForm({ mode: null, bundleId: null });
          updateHash(window.location.pathname);
        } else {
          setState((prev) => ({
            ...prev,
            confirmSaveChanges: true,
          }));
        }
        return null;
      case 'editBundle':
        setBundleForm({ mode: 'edit', bundleId: action.payload });
        updateHash(`#${action.payload}`);
        return null;
      case 'duplicateBundle':
        trackEvent({
          type: 'Offer duplicated',
          payload: {
            offerId: action.payload,
          },
        });
        setBundleForm({ mode: 'duplicate', bundleId: action.payload });
        return null;
      case 'editLandingPage':
        navigate(PATHS.OFFER_LANDING_PAGE_EDITOR(creatorId, action.payload));
        return null;
      case 'confirmDeleteBundle':
        setState((prev) => ({
          ...prev,
          confirmDelete: false,
          submittingBundle: true,
        }));
        deleteBundle();
        return null;
      case 'confirmArchiveBundle':
        setState((prev) => ({
          ...prev,
          confirmArchive: false,
          submittingBundle: true,
        }));
        archiveBundle();
        return null;
      case 'confirmPublishBundle':
        setState((prev) => ({ ...prev, confirmPublishContent: [] }));
        submitPublishBundle();
        return null;
      case 'redirect':
        trackRedirectEvent({
          path: action.payload,
          landingPagePath,
          showDiscountsPath,
          createDiscountPath,
          bundleId: state.bundleId,
        });
        navigate(action.payload);
        return null;
      case 'confirmSaveChanges':
        setState((prev) => ({ ...prev, confirmSaveChanges: true }));
        return null;
      case 'confirmRedirect':
        trackRedirectEvent({
          path: action.payload,
          landingPagePath,
          showDiscountsPath,
          createDiscountPath,
          bundleId: state.bundleId,
        });
        setState((prev) => ({
          ...prev,
          redirectPath: action.payload,
          confirmSaveChanges: true,
        }));
        return null;
      case 'confirmOfferChanges': {
        if (state?.redirectPath) {
          setState((prev) => ({ ...prev, submittingBundle: true }));
          saveBundle().then(() => navigate(state.redirectPath as string));
          return null;
        }
        setState((prev) => ({
          ...prev,
          confirmSaveChanges: false,
          submittingBundle: true,
        }));
        saveBundle().then(() => updateHash(window.location.pathname));
        return null;
      }
      case 'discardChanges': {
        setState((prev) => ({ ...prev, confirmSaveChanges: false }));
        if (state.redirectPath) {
          navigate(state.redirectPath);
          return null;
        }
        setBundleForm({ mode: null, bundleId: null });
        updateHash(window.location.pathname);
        return null;
      }
      case 'addOtpBundle':
        trackEvent({
          type: 'Offer created',
        });
        setBundleForm({ mode: 'create', bundleId: null });
        updateHash(window.location.pathname);
        return null;
    }
  };

  const publishBundle = () => {
    const notPublishedContent = sellableContent
      .filter((c) => content.includes(c.id))
      .filter((c) => !c.isPublished)
      .map((c) => c.name);

    const notPublishedPrograms = sellablePrograms
      .filter((c) => programs.includes(c.id))
      .filter((c) => !c.isPublished)
      .map((c) => c.name);

    if (isEmpty(notPublishedContent) && isEmpty(notPublishedPrograms)) {
      submitPublishBundle();
      return;
    }
    setState((prev) => ({
      ...prev,
      confirmPublishContent: [...notPublishedContent, ...notPublishedPrograms],
    }));
  };

  const saveBundle = handleSubmit(
    (values) => {
      state.formMode === 'edit' ? updateBundle(values) : createBundle(values);
    },
    () => {
      setState((prev) => ({ ...prev, submittingBundle: false }));
    },
  );

  const createBundle = async (
    values: BundleFormValues,
    status?: BundleStatus,
  ) => {
    const input = toCreateBundlePayload(creatorId, values, status);
    const id = uuid4();

    setState((prev) => ({
      ...prev,
      submittedNewBundle: [
        ...prev.submittedNewBundle,
        { id, title: input.title },
      ],
    }));

    try {
      const res = await modelValue?.createOtpBundle({
        variables: {
          input: input,
        },
        errorPolicy: 'all',
      });
      if (res?.data?.createOtpBundle?.warnings) {
        const error = res.data?.createOtpBundle?.warnings.message;
        setState((prev) => ({ ...prev, formError: error }));
        return;
      }
      if (res?.errors) {
        const errors = getMutationErrors(
          res.errors as MutationErrors,
          allPrograms,
          allContent,
        );
        setState((prev) => ({ ...prev, formError: errors }));
        return;
      }

      toast.success(`Offer ${values.name} was created successfully`);
      setState((prev) => ({
        ...prev,
        formMode: null,
        bundleId: null,
      }));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setState((prev) => ({
        ...prev,
        submittedNewBundle: prev.submittedNewBundle.filter((i) => i.id !== id),
        submittingBundle: false,
      }));
      modelValue?.refetchOtpBundles();
      modelValue?.refetchInfluencer();
    }
  };

  const updateBundle = async (
    values: BundleFormValues,
    status?: BundleStatus,
  ) => {
    if (!editedBundle) {
      toast.error('Cannot save offer updates');
      return;
    }

    const input = toUpdateBundlePayload(
      editedBundle.id,
      values,
      status || editedBundle.status,
      isAdmin,
    );
    setState((prev) => ({
      ...prev,
      submittedEditBundle: [
        ...prev.submittedEditBundle,
        { id: editedBundle.id, title: input.title },
      ],
    }));
    try {
      const res = await modelValue?.updateOtpBundle({
        variables: {
          input: input,
        },
        errorPolicy: 'all',
      });
      if (res?.data?.updateOtpBundle?.warnings) {
        const error = res.data?.updateOtpBundle?.warnings.message;
        setState((prev) => ({ ...prev, formError: error }));
        return;
      }
      if (res?.errors) {
        const errors = getMutationErrors(
          res.errors as MutationErrors,
          allPrograms,
          allContent,
        );
        setState((prev) => ({ ...prev, formError: errors }));
        return;
      }
      toast.success(`Offer ${editedBundle.title} was updated successfully`);
      setState((prev) => ({
        ...prev,
        formMode: null,
        bundleId: null,
      }));
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setState((prev) => ({
        ...prev,
        submittedEditBundle: prev.submittedEditBundle.filter(
          (i) => i.id !== editedBundle.id,
        ),
        submittingBundle: false,
      }));
      modelValue?.refetchOtpBundles();
      modelValue?.refetchInfluencer();
    }
  };

  const submitPublishBundle = handleSubmit(
    (values) => {
      trackEvent({
        type: 'Offer published',
        payload: {
          offerId: state.bundleId || 0,
          isFree,
        },
      });
      const publishError = validateContentForPublish(
        programs,
        features,
        content,
      );
      if (publishError) {
        setState((prev) => ({
          ...prev,
          formError: publishError,
          submittingBundle: false,
        }));
        return;
      }

      state.formMode === 'edit'
        ? updateBundle(values, BundleStatus.Published)
        : createBundle(values, BundleStatus.Published);
    },
    () => {
      setState((prev) => ({ ...prev, submittingBundle: false }));
    },
  );

  const archiveBundle = handleSubmit(
    (values) => {
      trackEvent({
        type: 'Offer archived',
        payload: {
          offerId: state.bundleId || 0,
        },
      });
      updateBundle(values, BundleStatus.Archived);
    },
    () => {
      setState((prev) => ({ ...prev, submittingBundle: false }));
    },
  );

  const deleteBundle = () => {
    trackEvent({
      type: 'Offer deleted',
      payload: {
        offerId: state.bundleId || 0,
      },
    });
    if (!state.bundleId) {
      toast.error('Cannot delete offer');
      return;
    }
    modelValue
      ?.deleteOtpBundle({
        variables: {
          bundleId: state.bundleId,
        },
      })
      .then(() => {
        toast.success(`Offer ${editedBundle?.title} was deleted successfully`);
        setState((prev) => ({
          ...prev,
          formMode: null,
          bundleId: null,
        }));
      })
      .catch((e) => {
        toast.error(e.message);
      })
      .finally(() => {
        setState((prev) => ({ ...prev, submittingBundle: false }));
        modelValue?.refetchOtpBundles();
        modelValue?.refetchInfluencer();
      });
  };

  const setBundleForm = ({
    mode,
    bundleId,
  }: {
    mode: FormMode;
    bundleId: number | null;
  }) => {
    const values =
      mode === 'create' || !bundleId
        ? formDefaultValues
        : getBundleFormDataByBundleId(
            bundleId,
            initialOtpBundles,
            rawContent,
            allPrograms,
            mode,
          );
    if (!values) return;

    reset(values, {
      keepValues: false,
      keepDirty: false,
      keepDirtyValues: false,
    });
    setState((prev) => ({
      ...prev,
      formMode: mode,
      bundleId,
      formError: null,
    }));
  };

  const extrasPath = PATHS.EXTRAS(creatorId);
  const otpPath = PATHS.NEW_PROGRAM(creatorId, { salesType: 'otp' });
  const landingPagePath = PATHS.OFFER_LANDING_PAGE_EDITOR(
    creatorId,
    state.bundleId || 0,
  );
  const showDiscountsPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'list',
    search: '"' + editedBundle?.title + '"',
  });
  const createDiscountPath = PATHS.PROMOTIONS(creatorId, {
    mode: 'create',
    offerId: '' + state.bundleId,
  });

  const formEditMode = state.formMode === 'edit';
  const savedProgramIds = toSavedProgramIds(editedBundle?.items);
  const savedContentIds = toSavedContentIds(editedBundle?.items);
  return {
    submittedEditBundle: state.submittedEditBundle,
    submittedNewBundle: state.submittedNewBundle,
    addOtpBundle: () => dispatch('addOtpBundle'),
    edit: (id: number) => dispatch('editBundle', id),
    duplicate: (id: number) => dispatch('duplicateBundle', id),
    editLP: (id: number) => dispatch('editLandingPage', id),
    formData: {
      creatorId,
      readonly: archived,
      showFeeControl: isAdmin && !isFree,
      control: control,
      open: !!state.formMode,
      cancel: () => dispatch('cancelBundleChanges'),
      title: archived
        ? 'Archived offer'
        : formEditMode
          ? 'Edit offer'
          : state.bundleId
            ? 'Duplicate offer'
            : 'Create new offer',
      cta: toFormCta({
        editMode: formEditMode,
        dispatch,
        landingPagePath,
        readonly: archived,
        bundleId: state.bundleId,
        published,
        submitting: state.submittingBundle,
        unsavedChanges: isDirty,
        wasPurchased,
        isFree,
      }),
      formError: state.formError,
      formErrorTitle: published
        ? 'Offer cannot be saved'
        : 'Offer cannot be published',
      data: {
        programs: {
          sectionTitle: 'Programs',
          emptyState: toFormProgramsEmptyState(dispatch, isDirty, otpPath),
          options: toFormPrograms(
            sellablePrograms,
            wasPurchased,
            savedProgramIds,
          ),
          toFormOnChange: toFormOnChangeArray,
          selectedItems: toFormSelectedPrograms(
            allPrograms,
            programs,
            wasPurchased,
            savedProgramIds,
            isFree,
          ),
        },
        content: {
          sectionTitle: 'Extras',
          emptyState: toFormContentEmptyState(dispatch, isDirty, extrasPath),
          options: toFormContent(
            sellableContent,
            wasPurchased,
            savedContentIds,
          ),
          toFormOnChange: toFormOnChangeArray,
          selectedItems: toFormSelectedContent(
            allContent,
            content,
            wasPurchased,
            savedContentIds,
            isFree,
          ),
        },
        features: {
          options: toFormFeatures(allFeatures),
          toFormOnChange: toFormOnChangeArray,
        },
      },
      discounts: formEditMode
        ? {
            count: discountsCount,
            onShow: () =>
              isDirty
                ? dispatch('confirmRedirect', showDiscountsPath)
                : dispatch('redirect', showDiscountsPath),
            onCreate: () =>
              isDirty
                ? dispatch('confirmRedirect', createDiscountPath)
                : dispatch('redirect', createDiscountPath),
          }
        : undefined,
      isFreeDisabled: published || archived,
      isOfferFree: isFree,
      onChangeIsFree,
      onChangeHideOffer,
    },
    deleteModal: {
      isOpen: !!state.confirmDelete,
      confirm: () => dispatch('confirmDeleteBundle'),
      cancel: () => setState((prev) => ({ ...prev, confirmDelete: false })),
    },
    archiveModal: {
      isOpen: !!state.confirmArchive,
      confirm: () => dispatch('confirmArchiveBundle'),
      cancel: () => setState((prev) => ({ ...prev, confirmArchive: false })),
    },
    publishModal: {
      isOpen: !isEmpty(state.confirmPublishContent),
      confirm: () => dispatch('confirmPublishBundle'),
      cancel: () =>
        setState((prev) => ({ ...prev, confirmPublishContent: [] })),
      draftItems: state.confirmPublishContent || [],
    },
    unsavedChangesModal: {
      isOpen: !!state.confirmSaveChanges,
      saveChanges: () => dispatch('confirmOfferChanges'),
      discard: () => dispatch('discardChanges'),
      cancel: () =>
        setState((prev) => ({ ...prev, confirmSaveChanges: false })),
    },
  };
};
